import { getDisciplineByKey } from 'constants/disciplines';
import Immutable from 'immutable';
import { MappedSheetQuery } from 'records/MappedSheetQuery';
import { MappedSheetVersionMap } from 'records/MappedSheetVersion';
import { Sheet, SheetId, SheetMap } from 'records/Sheet';
import { SheetVersion } from 'records/SheetVersion';
import { Discipline } from 'records/Discipline';

interface SortHandlers {
  [key: string]: (
    sheets: SheetMap,
    mappedSheets: MappedSheetVersionMap,
    direction: string
  ) => SheetMap;
}

const sortHandlers: SortHandlers = {
  creationDate: (
    sheets: SheetMap,
    mappedSheets: MappedSheetVersionMap,
    direction
  ): SheetMap => {
    const sortedMappedSheetsByPageId = mappedSheets
      .sort((sheetA, sheetB) => {
        const a =
          direction === 'ascending'
            ? sheetA.imagesource?.calibrationconfig.creationtimestamp || 0
            : sheetB.imagesource?.calibrationconfig.creationtimestamp || 0;
        const b =
          direction === 'ascending'
            ? sheetB.imagesource?.calibrationconfig.creationtimestamp || 0
            : sheetA.imagesource?.calibrationconfig.creationtimestamp || 0;

        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }

        return 0;
      })
      .keySeq();

    let sortedSheets = Immutable.OrderedMap<SheetId, Sheet>();

    const latestSheetVersions = sheets
      .valueSeq()
      .map((sheet) => sheet.versions.last(new SheetVersion()))
      .toArray();

    sortedMappedSheetsByPageId.forEach((pageId) => {
      const sheetVersion = latestSheetVersions.find(
        (sv) => sv.pageid === pageId
      );

      if (sheetVersion?.sheetnumber) {
        const sheet = sheets.get(sheetVersion.sheetnumber);

        if (sheet) {
          sortedSheets = sortedSheets.set(sheet.sheetid, sheet);
        }
      }
    });

    return sortedSheets;
  },

  discipline: (sheets: SheetMap, _, direction): SheetMap => {
    const sortedSheets = sheets.sort((sheetA, sheetB): number => {
      const a =
        direction === 'ascending'
          ? sheetA.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline())
          : sheetB.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline());
      const b =
        direction === 'ascending'
          ? sheetB.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline())
          : sheetA.versions
              .last(new SheetVersion())
              .get('sheetDiscipline', new Discipline());

      if (a.get('sortOrder') > b.get('sortOrder')) {
        return 1;
      }

      if (b.get('sortOrder') > a.get('sortOrder')) {
        return -1;
      }

      return 0;
    });

    return sortedSheets;
  },
};

function sortSheets(
  sheets: SheetMap,
  mappedSheets: MappedSheetVersionMap,
  mappedSheetQuery: MappedSheetQuery
): SheetMap {
  const { sortField, sortDirection } = mappedSheetQuery;

  if (sortHandlers[sortField]) {
    sheets = sortHandlers[sortField](sheets, mappedSheets, sortDirection);
  }

  return sheets;
}

function searchSheets(
  sheets: SheetMap,
  mappedSheetQuery: MappedSheetQuery
): SheetMap {
  const { search } = mappedSheetQuery;

  if (search.length === 0) {
    return sheets;
  }

  const sanitizeString = (sheetNumber: string) =>
    sheetNumber.toLowerCase().replaceAll(/[.-\s]+/g, '');

  const sanitizedSearch = sanitizeString(search);

  const results = sheets.filter((sheet) => {
    const discipline = getDisciplineByKey(
      sheet.versions.last(new SheetVersion()).discipline
    );

    const sanitizedDiscipline = sanitizeString(discipline);

    if (sanitizedDiscipline.indexOf(sanitizedSearch) >= 0) {
      return true;
    }

    const sanitizedSheetTitle = sanitizeString(
      sheet.versions.last(new SheetVersion()).sheettitle
    );

    if (sanitizedSheetTitle.indexOf(sanitizedSearch) >= 0) {
      return true;
    }

    const sanitizedSheetNumber = sanitizeString(
      sheet.versions.last(new SheetVersion()).sheetnumber
    );

    return sanitizedSheetNumber.indexOf(sanitizedSearch) >= 0;
  });

  return results;
}

function filterSheets(
  sheets: SheetMap,
  sheetQuery: MappedSheetQuery
): SheetMap {
  let filteredSheets = sheets;

  // Disciplines
  if (sheetQuery.disciplines.size > 0) {
    filteredSheets = filteredSheets.filter((sheet) => {
      const sheetVersion = sheet.versions.last(new SheetVersion());

      return sheetQuery.disciplines.has(
        sheetVersion.getIn(['sheetDiscipline', 'designator'])
      );
    });
  }

  return filteredSheets;
}

export default function querySheets(
  sheets: SheetMap,
  mappedSheetVersions: MappedSheetVersionMap,
  mappedSheetQuery: MappedSheetQuery
): SheetMap {
  const mappedSheets = sheets.filter((sheet) => {
    const latestSheet = sheet.versions.last(new SheetVersion());
    return mappedSheetVersions.has(latestSheet.pageid);
  });

  const searchedSheets = searchSheets(mappedSheets, mappedSheetQuery);

  const filteredSheets = filterSheets(searchedSheets, mappedSheetQuery);

  const sortedSheets = sortSheets(
    filteredSheets,
    mappedSheetVersions,
    mappedSheetQuery
  );

  return sortedSheets;
}
